import type { RefObject } from 'react';
import { useCallback, useEffect, useRef } from 'react';

import { FADE_RAMP_PX } from './chatScroll.constants';

interface UseBottomFadeArgs {
  scrollRef: RefObject<HTMLDivElement>;
  getTailEl: () => HTMLElement | null;
}

export const useBottomFade = ({ scrollRef, getTailEl }: UseBottomFadeArgs) => {
  const bottomFadeRef = useRef<HTMLDivElement>(null);

  // Read the latest getTailEl through a ref so callers may pass an inline
  // closure without destabilising updateFadeOpacity — the scroll hook's
  // observer/listener effects depend on its identity.
  const getTailElRef = useRef(getTailEl);
  useEffect(() => {
    getTailElRef.current = getTailEl;
  });

  const updateFadeOpacity = useCallback(() => {
    const scroller = scrollRef.current;
    if (!scroller) return;
    const tail = getTailElRef.current();
    const distanceFromBottom = tail
      ? tail.getBoundingClientRect().bottom - scroller.getBoundingClientRect().bottom
      : scroller.scrollHeight - scroller.scrollTop - scroller.clientHeight;
    const ramp = (px: number) => String(Math.min(1, Math.max(0, px / FADE_RAMP_PX)));
    if (bottomFadeRef.current) {
      bottomFadeRef.current.style.opacity = ramp(distanceFromBottom);
    }
  }, [scrollRef]);

  return { bottomFadeRef, updateFadeOpacity };
};
